import { normalizeB2bUrl, normalizeURL } from './urlNormalizer.js';
import { replaceAll } from '../helpers/string.js';
import { resolveDeployPrefix } from '../initialization/deployPrefixResolver.js';
import { season$, stpShortTitles$ } from '../stores/store-main.js';
import { SWISS_EXTENT } from '../helpers/constants.js';
import { landCodeToApiCode, FLEXIBLE_GRAPHHOPPER_PROFILES, } from '../layers/layerHelpers.js';
import { BASE_API_URL, GRAPHHOPPER_URL, SCHWEIZMOBIL_ACCOUNT_URL, SCHWEIZMOBIL_PLUS_URL, } from '../envs.js';
import { legendUrlFragment, slowupLegendUrlFragment, landUrlFragment, routeCategoriesUrlFragment, seasonsUrlFragment, mapUrlFragment, routeUrlFragment, segmentUrlFragment, schmPlusInstructionsUrlFragment, schmPlusManageTourListUrlFragment, schmPlusAccountUrlFragment, } from './urlParams.js';
import { getLayersForLayertree, getOidcLoginEnabled } from './urlSearchParams.js';
import { getPersistentSearchParams } from './initialSearchParams.js';
import { hasAuthCookie } from '../helpers/tokenOrCookie.js';
import { mergeUrlParms } from '../helpers/url.js';
import { readStoredMapState, getStoredLayersForSeason, } from '../helpers/mapStateStorage.js';
const deployPrefix = resolveDeployPrefix();
function psp(url, sep = '?') {
    let persistentParams = getPersistentSearchParams();
    if (sep === '&') {
        const urlParamsStr = url.split('?')[1];
        const urlBase = url.split('?')[0];
        if (urlParamsStr.length > 1) {
            persistentParams = mergeUrlParms(new URLSearchParams(urlParamsStr), getPersistentSearchParams());
            return `${urlBase}?${persistentParams.toString()}`;
        }
    }
    const p = persistentParams.toString();
    if (p) {
        return `${url}${sep}${p}`;
    }
    return url;
}
export function createLegendURL(lang) {
    return psp(`${deployPrefix}/${lang}/${legendUrlFragment[lang]}`);
}
export function createSlowupLegendURL(lang) {
    return psp(`${deployPrefix}/${lang}/${slowupLegendUrlFragment[lang]}`);
}
function createLandURLPrefix(lang, land, category) {
    const landPath = `${deployPrefix}/${lang}/${landUrlFragment[land][lang]}`;
    if (category) {
        return `${landPath}/${routeCategoriesUrlFragment[category][lang]}`;
    }
    return landPath;
}
export function createLandURL(lang, land, category) {
    return psp(createLandURLPrefix(lang, land, category));
}
export function createMainURL(lang) {
    return psp(`${deployPrefix}/${lang}`);
}
export function createSeasonURL(lang, season) {
    return psp(`${deployPrefix}/${lang}/${seasonsUrlFragment[season][lang]}`);
}
export function createMapURL(options) {
    const { lang, season, land, resetPosition } = options;
    let mapUrl = `${deployPrefix}/${lang}/${mapUrlFragment[lang]}`;
    const paramsStr = options.params?.toString();
    if (!season) {
        return paramsStr?.length ? psp(`${mapUrl}?${paramsStr}`, '&') : psp(mapUrl);
    }
    mapUrl = `${mapUrl}?season=${season}`;
    if (land) {
        const stored = readStoredMapState();
        if (stored && !options.embed) {
            const visibleLayers = getStoredLayersForSeason(stored, season ?? 'summer');
            let layers = visibleLayers.join(',');
            if (!layers.includes(landCodeToApiCode[land])) {
                layers = `${layers},${landCodeToApiCode[land]}`;
            }
            const additionalLayers = stored.additionalRouteLayers;
            mapUrl = `${mapUrl}&layers=${layers}&bgLayer=${stored.bgLayer}&detours=${additionalLayers.detours ? 'yes' : 'no'}&photos=${additionalLayers.photos ? 'yes' : 'no'}&logos=${additionalLayers.logos ? 'yes' : 'no'}&shooting=${additionalLayers.shooting ? 'yes' : 'no'}`;
        }
        else {
            mapUrl = `${mapUrl}&layers=${landCodeToApiCode[land]}`;
        }
    }
    if (resetPosition) {
        mapUrl = `${mapUrl}&fitBbox=true&bbox=${SWISS_EXTENT.join('-')}&bboxPadding=10-10-10-10`;
    }
    if (paramsStr?.length > 1) {
        mapUrl = `${mapUrl}&${paramsStr}`;
    }
    return psp(mapUrl, '&');
}
export function createSightseeingPlaceUrl(title, lang, layer, id) {
    return psp(`${deployPrefix}/${lang}/${layer}-${id}-${replaceAll(title.toLowerCase(), ' ', '-')}`);
}
export function createPointOfInterestUrl(lang, layer, id) {
    return psp(`${deployPrefix}/${lang}/${layer}-${id}`);
}
export function getUrlForLang(lang, detectedLang, season) {
    const path = location.pathname.replace(detectedLang, lang);
    const deployPrefix = resolveDeployPrefix();
    const stpTitles = stpShortTitles$.getValue();
    if (stpTitles) {
        return `${location.origin}/${lang}/${stpTitles[lang]}${location.search}${location.hash}`;
    }
    const { normalizedURL } = normalizeURL(`${location.origin}/${path}/${location.search}`, lang, season, deployPrefix);
    return normalizedURL;
}
export function createStaticPageUrl(lang, titleShort, urlSearchParams) {
    const uspString = urlSearchParams ? urlSearchParams.toString() : '';
    return psp(`${deployPrefix}/${lang}/${titleShort}${uspString ? '?' + uspString : ''}`, uspString ? '&' : '?');
}
export function createStaticPageUrlB2C(lang, titleShort) {
    let prefix = undefined;
    const loc = document?.location;
    if (loc && loc.hostname.endsWith('.info')) {
        prefix = `${loc.protocol}//${loc.hostname.replace('.info', '.ch')}`;
    }
    return psp([prefix, deployPrefix, lang, titleShort]
        .filter((e) => e !== undefined)
        .join('/'));
}
export function createUrlFromTeaser(lang, t, season, land) {
    const text = t.urlText
        ? t.urlText
        : `En savoir plus`;
    const titleShort = t.staticPage?.titleShort || t.topicPage?.titleShort;
    if (titleShort) {
        const defaultStaticPageTitle = t.defaultStaticPage?.titleShort
            ? `#${t.defaultStaticPage?.titleShort}`
            : '';
        const url = `${deployPrefix}/${lang}/${titleShort}?origin=${land || season}${defaultStaticPageTitle}`;
        return [psp(url, '&'), text];
    }
    if (t.urlLink) {
        const link = t.urlLink.startsWith('/')
            ? psp(`${deployPrefix}${t.urlLink}`)
            : t.urlLink;
        return [link, text];
    }
    return ['', ''];
}
function createRouteURLPrefix(lang, land, routeNb) {
    return `${createLandURLPrefix(lang, land)}/${routeUrlFragment[lang]}-${routeNb}`;
}
export function createSegmentURLPrefix(lang, land, routeNb, segmentNb) {
    return `${createRouteURLPrefix(lang, land, routeNb)}/${segmentUrlFragment[lang]}-${segmentNb}`;
}
export function createRouteURL(lang, land, routeNb, keepLayersAndNoScroll = false) {
    const extra = keepLayersAndNoScroll ? '?keepLayers=true&noScroll=true' : '';
    return psp(`${createRouteURLPrefix(lang, land, routeNb)}${extra}`, extra ? '&' : '?');
}
export function createSegmentURL(lang, land, routeNb, segmentNb, keepLayersAndNoScroll = false) {
    const extra = keepLayersAndNoScroll ? '?keepLayers=true&noScroll=true' : '';
    return psp(`${createSegmentURLPrefix(lang, land, routeNb, segmentNb)}${extra}`, extra ? '&' : '?');
}
export function createMapUrlFromBbox(options) {
    const { lang, params, bbox, keepLayersFromStore, highlightPointCoordinates, bboxPadding, bboxResolution, } = options;
    const { bgLayer, detours, photos, logos } = Object.fromEntries(params);
    const newParams = new URLSearchParams();
    newParams.set('bbox', bbox.join('-'));
    if (bboxPadding?.length) {
        newParams.set('bboxPadding', bboxPadding.join('-'));
    }
    if (typeof bboxResolution === 'number' && bboxResolution >= 1) {
        newParams.set('bboxResolution', String(bboxResolution));
    }
    newParams.set('fitBbox', 'true');
    newParams.set('bgLayer', bgLayer);
    newParams.set('detours', detours);
    newParams.set('layers', getLayersForLayertree(params).join(','));
    newParams.set('photos', photos);
    newParams.set('logos', logos);
    newParams.set('season', season$.getValue());
    newParams.set('keepLayers', String(keepLayersFromStore));
    newParams.set('highlightPointCoordinates', String(highlightPointCoordinates.join('-')));
    return psp(`${deployPrefix}/${lang}/map?${newParams.toString()}`, '&');
}
export function createUpdateAccountURL(lang) {
    if (getOidcLoginEnabled()) {
        return SCHWEIZMOBIL_ACCOUNT_URL;
    }
    else {
        const accountUrlPart = lang === 'en' && hasAuthCookie()
            ? schmPlusAccountUrlFragment['de']
            : schmPlusAccountUrlFragment[lang];
        return `${SCHWEIZMOBIL_PLUS_URL}/${accountUrlPart}`;
    }
}
export function createAccountURL() {
    if (getOidcLoginEnabled()) {
        return SCHWEIZMOBIL_ACCOUNT_URL;
    }
    else {
        return SCHWEIZMOBIL_PLUS_URL;
    }
}
export function createAccountRegistrationURL() {
    if (getOidcLoginEnabled()) {
        return `${SCHWEIZMOBIL_ACCOUNT_URL}/register`;
    }
    else {
        return `${SCHWEIZMOBIL_PLUS_URL}/${`suissemobileplus`}/registration.html`;
    }
}
export function createToursURL(lang) {
    return psp(`${deployPrefix}/${lang}/tours`);
}
export function createTourURL(id, type, lang) {
    return psp(`${deployPrefix}/${lang}/${type === 'normal' ? 'tour' : 'recordedtrack'}/${id}`);
}
export function createNewTourURL(lang) {
    return psp(`${deployPrefix}/${lang}/tour`);
}
export function createLoginPageURL(lang) {
    return psp(`${deployPrefix}/${lang}/login`);
}
export function createGraphhopperRouterURL(profile) {
    const base = `${GRAPHHOPPER_URL}/route?profile=${profile}&type=json&elevation=true&way_point_max_distance=0&instructions=false&points_encoded=true&details=surface&details=hiking_category&details=structure`;
    const flexible = FLEXIBLE_GRAPHHOPPER_PROFILES.has(profile);
    return flexible ? `${base}&ch.disable=true` : base;
}
export function createGraphhopperSnapperURL() {
    return `${GRAPHHOPPER_URL}/nearest?elevation=true`;
}
export function createSchmPlusInstructionsUrl(lang) {
    return `${deployPrefix}/${lang}/${schmPlusInstructionsUrlFragment[lang]}`;
}
export function createSchmPlusManageTourListUrl(lang) {
    return `${deployPrefix}/${lang}/${schmPlusManageTourListUrlFragment[lang]}`;
}
export function createRouteSegmentGpxURL(lang, land, routeNumber, segmentNumber) {
    return `${BASE_API_URL}/api/6/routes_and_segments_gpx/${lang}/${land}/${routeNumber}/${segmentNumber}.gpx`;
}
export function getB2bUrlForLang(lang, detectedLang) {
    const path = location.pathname.replace(detectedLang, lang);
    const deployPrefix = resolveDeployPrefix();
    const stpTitles = stpShortTitles$.getValue();
    if (stpTitles) {
        return `${location.origin}/${lang}/${stpTitles[lang]}${location.search}${location.hash}`;
    }
    const normalizedURL = normalizeB2bUrl(`${location.origin}/${path}${location.search}`, deployPrefix, lang);
    return normalizedURL;
}
export function createLoginURL(lang) {
    return `/${lang}/login?redirect=${encodeURIComponent(`${location.pathname}${location.search}`)}`;
}
export function createSwitzerlandmobilityPlusURL(lang) {
    return `/${lang}/${`suissemobile-plus`}`;
}
//# sourceMappingURL=urlCreate.js.map